import { ReactiveVar, makeVar } from '@apollo/client';

import { AuthErrorType } from '../enums';
import { AuthenticationUser } from '../third-parties';
import { OAuthLifecycleAdapter } from '../third-parties/firebase/firebase.utils';

type AuthenticationServiceState = {
  authenticationUser: AuthenticationUser | null;
  loading: boolean;
};

type AuthenticationState = {
  authenticationServiceState: AuthenticationServiceState;
  userReconciled: boolean;
  isAuthenticating: boolean;
  errorType: AuthErrorType | null;
};

type AuthenticationStateVar = ReactiveVar<AuthenticationState>;

const defaultAuthenticationState: AuthenticationState = {
  authenticationServiceState: { authenticationUser: null, loading: true },
  userReconciled: false,
  isAuthenticating: false,
  errorType: null,
};

export const authenticationStateVar: AuthenticationStateVar = makeVar<AuthenticationState>(defaultAuthenticationState);

export const updateAuthenticationUser = (user: AuthenticationUser | null): AuthenticationState => {
  const currentAuthenticationState = authenticationStateVar();
  return authenticationStateVar({
    ...currentAuthenticationState,
    authenticationServiceState: { authenticationUser: user, loading: false },
  });
};

export const updateAuthErrorType = (errorType: AuthErrorType | null): AuthenticationState => {
  const currentAuthenticationState = authenticationStateVar();
  return authenticationStateVar({ ...currentAuthenticationState, errorType });
};

export const updateUserReconciled = (): AuthenticationState => {
  const currentAuthenticationState = authenticationStateVar();
  return authenticationStateVar({ ...currentAuthenticationState, userReconciled: true });
};

export const resetAuthentication = (): AuthenticationState =>
  authenticationStateVar({
    ...defaultAuthenticationState,
    authenticationServiceState: { authenticationUser: null, loading: false },
  });

export const setIsAuthenticating = (isAuthenticating: boolean): AuthenticationState => {
  const currentAuthenticationState = authenticationStateVar();
  return authenticationStateVar({
    ...currentAuthenticationState,
    isAuthenticating: isAuthenticating,
  });
};

export const authenticationStateOAuthAdapter: OAuthLifecycleAdapter = {
  onStart: () => {
    setIsAuthenticating(true);
    // Drop a reason no page ever displayed, so it cannot surface against this attempt instead of the
    // one that produced it. Clearing what is already on screen is the reading hook's job, since only it
    // knows about messages a form wrote directly.
    updateAuthErrorType(null);
  },
  onSuccess: () => {
    setIsAuthenticating(false);
  },
  // Recording the reason here is what makes a failed sign-in visible at all for callers that do not
  // handle the rejection themselves: the login and signup forms both render whatever this holds.
  onError: (reason) => {
    setIsAuthenticating(false);
    if (reason) {
      updateAuthErrorType(reason);
    }
  },
};
